import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Users,
  UserPlus,
  UserMinus,
  AlertTriangle,
  Clock,
  TrendingUp,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';

/**
 * Professional Analytics Dashboard
 * Real-time insights with auto-scaling charts
 */
const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.depot_id) {
      loadDashboardData();
    } else if (user !== undefined) {
      setLoading(false);
    }
  }, [timeRange, user]);

  const loadDashboardData = async () => {
    if (!user?.depot_id) return;
    
    setLoading(true);
    try {
      const now = new Date();
      const rangeMs = {
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000,
      };
      const startDate = new Date(now - rangeMs[timeRange]);

      // Get all sessions in time range
      const { data: allSessions } = await supabase
        .from('site_sessions')
        .select('*')
        .eq('depot_id', user.depot_id)
        .gte('checked_in_at', startDate.toISOString())
        .order('checked_in_at', { ascending: true });

      // Get currently on site
      const { data: activeSessions } = await supabase
        .from('site_sessions')
        .select('*')
        .eq('depot_id', user.depot_id)
        .is('checked_out_at', null);

      // Get today's sessions
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const { data: todaySessions } = await supabase
        .from('site_sessions')
        .select('*')
        .eq('depot_id', user.depot_id)
        .gte('checked_in_at', todayStart.toISOString());

      // Get incidents
      const { data: incidents } = await supabase
        .from('incidents')
        .select('*')
        .eq('depot_id', user.depot_id)
        .gte('created_at', startDate.toISOString());

      // Calculate KPIs
      const currentlyOnSite = activeSessions?.length || 0;
      const checkInsToday = todaySessions?.length || 0;
      const checkOutsToday = todaySessions?.filter(s => s.checked_out_at)?.length || 0;
      const activeIncidents = incidents?.filter(i => ['open', 'investigating'].includes(i.status))?.length || 0;
      
      // Calculate average session time
      const completedSessions = allSessions?.filter(s => s.checked_out_at) || [];
      let avgSessionTime = 0;
      if (completedSessions.length > 0) {
        const totalMinutes = completedSessions.reduce((sum, session) => {
          const checkIn = new Date(session.checked_in_at);
          const checkOut = new Date(session.checked_out_at);
          const minutes = (checkOut - checkIn) / 1000 / 60;
          return sum + minutes;
        }, 0);
        avgSessionTime = Math.round(totalMinutes / completedSessions.length);
      }

      // Generate daily check-in data (last 7 days)
      const dailyData = [];
      const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
      
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);
        
        const daySessions = allSessions?.filter(s => {
          const sessionDate = new Date(s.checked_in_at);
          return sessionDate >= date && sessionDate < nextDate;
        }) || [];

        dailyData.push({
          name: date.toLocaleDateString('en-US', { weekday: 'short' }),
          'Check-ins': daySessions.length,
          'Check-outs': daySessions.filter(s => s.checked_out_at).length,
        });
      }

      // Hourly check-in activity (for today)
      const hourlyData = Array.from({ length: 24 }, (_, hour) => {
        const hourSessions = todaySessions?.filter(s => {
          const sessionHour = new Date(s.checked_in_at).getHours();
          return sessionHour === hour;
        }) || [];
        
        return {
          hour: `${hour.toString().padStart(2, '0')}:00`,
          count: hourSessions.length,
        };
      });

      // Visitor type breakdown
      const visitorTypes = [
        {
          name: 'Contractors',
          value: allSessions?.filter(s => s.person_type === 'contractor').length || 0,
          color: '#3b82f6',
        },
        {
          name: 'Visitors',
          value: allSessions?.filter(s => s.person_type === 'visitor').length || 0,
          color: '#10b981',
        },
        {
          name: 'Employees',
          value: allSessions?.filter(s => s.person_type === 'employee').length || 0,
          color: '#f59e0b',
        },
      ];

      // Incident severity breakdown
      const incidentSeverity = [
        {
          name: 'Critical',
          value: incidents?.filter(i => i.severity === 'critical').length || 0,
          color: '#7c3aed', // Deep purple
        },
        {
          name: 'High',
          value: incidents?.filter(i => i.severity === 'high').length || 0,
          color: '#ef4444', // Professional red
        },
        {
          name: 'Medium',
          value: incidents?.filter(i => i.severity === 'medium').length || 0,
          color: '#f59e0b', // Amber
        },
        {
          name: 'Low',
          value: incidents?.filter(i => i.severity === 'low').length || 0,
          color: '#10b981', // Emerald green
        },
      ].filter(item => item.value > 0);

      // Monthly trend
      const monthlyData = [];
      const daysInMonth = timeRange === '30d' ? 30 : 7;
      for (let i = daysInMonth - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);
        
        const daySessions = allSessions?.filter(s => {
          const sessionDate = new Date(s.checked_in_at);
          return sessionDate >= date && sessionDate < nextDate;
        }) || [];

        monthlyData.push({
          day: date.getDate(),
          count: daySessions.length,
        });
      }

      // Calculate average incident resolution time
      const resolvedIncidents = incidents?.filter(i => i.resolved_at) || [];
      let avgResolutionHours = 0;
      if (resolvedIncidents.length > 0) {
        const totalHours = resolvedIncidents.reduce((sum, incident) => {
          const created = new Date(incident.created_at);
          const resolved = new Date(incident.resolved_at);
          const hours = (resolved - created) / 1000 / 60 / 60;
          return sum + hours;
        }, 0);
        avgResolutionHours = Math.round(totalHours / resolvedIncidents.length);
      }

      setStats({
        kpis: {
          currentlyOnSite,
          checkInsToday,
          checkOutsToday,
          activeIncidents,
          avgSessionTime: `${Math.floor(avgSessionTime / 60)}h ${avgSessionTime % 60}m`,
        },
        charts: {
          dailyCheckIns: dailyData,
          hourlyActivity: hourlyData,
          visitorTypes,
          incidentSeverity,
          monthlyTrend: monthlyData,
          avgResolutionHours,
        },
      });
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user?.depot_id && !loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-foreground">No site assigned</p>
          <p className="mt-2 text-sm text-muted-foreground">Your account is not yet linked to a site. Contact your system administrator to be assigned to a depot.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Real-time site activity and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 Hours</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadDashboardData} variant="outline" className="bg-white">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {/* Currently On Site */}
        <Card className="border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 hover:border-blue-400 cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.kpis?.currentlyOnSite || 0}
            </div>
            <p className="text-sm text-gray-600 mt-1">On Site</p>
          </CardContent>
        </Card>

        {/* Check-ins Today */}
        <Card className="border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 hover:border-green-400 cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-green-100 rounded-lg">
                <UserPlus className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.kpis?.checkInsToday || 0}
            </div>
            <p className="text-sm text-gray-600 mt-1">Check-ins Today</p>
          </CardContent>
        </Card>

        {/* Check-outs Today */}
        <Card className="border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 hover:border-purple-400 cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-purple-100 rounded-lg">
                <UserMinus className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.kpis?.checkOutsToday || 0}
            </div>
            <p className="text-sm text-gray-600 mt-1">Check-outs Today</p>
          </CardContent>
        </Card>

        {/* Active Incidents */}
        <Card className="border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 hover:border-red-400 cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.kpis?.activeIncidents || 0}
            </div>
            <p className="text-sm text-gray-600 mt-1">Active Incidents</p>
          </CardContent>
        </Card>

        {/* Average Session Time */}
        <Card className="border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 hover:border-cyan-400 cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-cyan-100 rounded-lg">
                <Clock className="h-6 w-6 text-cyan-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.kpis?.avgSessionTime || '0m'}
            </div>
            <p className="text-sm text-gray-600 mt-1">Avg Session Time</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Daily Check-ins Trend */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Site Occupancy Trend
            </CardTitle>
            <CardDescription>Daily check-ins and check-outs</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats?.charts?.dailyCheckIns}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Legend />
                <Bar dataKey="Check-ins" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Check-outs" fill="#ec4899" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Incidents by Severity */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Incidents by Severity
            </CardTitle>
            <CardDescription>Breakdown of incident priorities</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats?.charts?.incidentSeverity}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats?.charts?.incidentSeverity?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Visitor Type Breakdown */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-green-600" />
              Visitor Type Breakdown
            </CardTitle>
            <CardDescription>Distribution by person type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats?.charts?.visitorTypes}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats?.charts?.visitorTypes?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Hourly Activity */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-600" />
              Check-in Activity by Hour
            </CardTitle>
            <CardDescription>Peak hours for today</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats?.charts?.hourlyActivity?.filter(d => d.count > 0)}>
                <defs>
                  <linearGradient id="colorActivity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#1d4ed8" stopOpacity={0.7}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="count" fill="url(#colorActivity)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-cyan-600" />
              {timeRange === '30d' ? 'Monthly' : 'Weekly'} Check-ins Trend
            </CardTitle>
            <CardDescription>Activity over time with auto-scaling</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats?.charts?.monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#06b6d4" 
                  strokeWidth={3}
                  dot={{ fill: '#06b6d4', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Incident Resolution Stats */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Average Incident Resolution Time
            </CardTitle>
            <CardDescription>Time to resolve incidents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center h-[300px]">
              <div className="text-6xl font-bold text-orange-600">
                {stats?.charts?.avgResolutionHours || 0}h
              </div>
              <p className="text-gray-600 mt-4 text-lg">Average Resolution Time</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
